import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './CreateInvestment.css';
import PageTitle from '../../components/UI/PageTitle';
import { getInvestmentById, updateInvestment } from '../../api/Index';

const EditInvestment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [investment, setInvestment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    amount: '',
    expectedReturn: '',
    startDate: '',
    endDate: '',
    status: 'active'
  });

  useEffect(() => {
    const fetchInvestmentData = async () => {
      console.log('EditInvestment component mounted');
      console.log('Current location:', location);
      setLoading(true);
      setError(null);
      try {
        const searchParams = new URLSearchParams(location.search);
        const investmentId = searchParams.get('id');
        console.log('Extracted investment ID:', investmentId);
        
        if (!investmentId) {
          console.error('No investment ID found in URL parameters');
          setError('Investment ID not provided');
          return;
        }

        console.log('Calling getInvestmentById with ID:', investmentId);
        const response = await getInvestmentById(investmentId);
        console.log('Investment details response:', response);
        
        const investmentData = response.data;
        console.log('Parsed investment data:', investmentData);
        setInvestment(investmentData);
        
        // Pre-fill form data
        const formDataToSet = {
          name: investmentData.name || '',
          description: investmentData.description || '',
          amount: investmentData.amount || investmentData.amountInvested || '',
          expectedReturn: investmentData.expectedReturn || '',
          startDate: investmentData.startDate ? new Date(investmentData.startDate).toISOString().split('T')[0] : '',
          endDate: investmentData.endDate ? new Date(investmentData.endDate).toISOString().split('T')[0] : '',
          status: investmentData.status || 'active'
        };
        console.log('Setting form data:', formDataToSet);
        setFormData(formDataToSet);
        
      } catch (err) {
        console.error('Error fetching investment details:', err);
        console.error('Error response:', err.response);
        console.error('Error message:', err.message);
        setError(err.response?.data?.msg || err.message || 'Failed to load investment details');
      } finally {
        console.log('Finished loading investment data');
        setLoading(false);
      }
    };

    fetchInvestmentData();
  }, [location.search]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleStatusClick = (status) => {
    setFormData({
      ...formData,
      status
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError(null);
    
    try {
      const searchParams = new URLSearchParams(location.search);
      const investmentId = searchParams.get('id');
      
      if (!investmentId) {
        setError('Investment ID not provided');
        return;
      }

      const updateData = {
        name: formData.name,
        description: formData.description,
        amount: parseFloat(formData.amount),
        expectedReturn: parseFloat(formData.expectedReturn),
        startDate: formData.startDate,
        endDate: formData.endDate,
        status: formData.status
      };

      const response = await updateInvestment(investmentId, updateData);
      console.log('Investment updated successfully:', response.data);
      
      // Navigate back to investments page
      navigate('/investments');
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to update investment');
      console.error('Error updating investment:', err);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div>
        <PageTitle title='Edit Investment' />
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Loading investment details...</p>
          <p style={{ color: '#666', fontSize: '14px' }}>Please wait while we fetch the investment data.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageTitle title='Edit Investment' />
        <div style={{ padding: '20px', backgroundColor: '#ffe6e6', border: '1px solid #ff0000', borderRadius: '4px', margin: '20px' }}>
          <h3 style={{ color: 'red', margin: '0 0 10px 0' }}>Error Loading Investment</h3>
          <p style={{ color: 'red', margin: '0' }}>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{ marginTop: '10px', padding: '8px 16px', backgroundColor: '#ff0000', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!investment) {
    return (
      <div>
        <PageTitle title='Edit Investment' />
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Investment not found.</p>
          <p style={{ color: '#666', fontSize: '14px' }}>The requested investment could not be found.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div>
        <PageTitle title='Edit Investment' />
        <div className="create-investment-container">
            <div className="form-container">
                <form className="create-investment-form" onSubmit={handleSubmit}>

                    <div className="input-group">
                        <label className="input-label">Name *</label>
                        <input
                            type="text"
                            className="input"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter Investment Name"
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Amount Invested *</label>
                        <input
                            type="number"
                            className="input"
                            name="amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                            placeholder="Enter Invested Amount"
                            required
                        />
                    </div>

                    <div className='status-group'>
                        <label className="input-label">Investment Status</label>
                        <div className='status-label-group'>
                            <button 
                              type="button"
                              className={formData.status === 'pending' ? 'active' : ''}
                              onClick={() => handleStatusClick('pending')}
                            >
                              Pending
                            </button>
                            <button 
                              type="button"
                              className={formData.status === 'active' ? 'active' : ''}
                              onClick={() => handleStatusClick('active')}
                            >
                              Active
                            </button>
                            <button 
                              type="button"
                              className={formData.status === 'completed' ? 'active' : ''}
                              onClick={() => handleStatusClick('completed')}
                            >
                              Completed
                            </button>
                            <button 
                              type="button"
                              className={formData.status === 'cancelled' ? 'active' : ''}
                              onClick={() => handleStatusClick('cancelled')}
                            >
                              Cancelled
                            </button>
                        </div>
                    </div>

                    <div className="input-group">
                        <label className="input-label">Expected Return *</label>
                        <input
                            type="number"
                            className="input"
                            name="expectedReturn"
                            value={formData.expectedReturn}
                            onChange={handleInputChange}
                            placeholder="Enter Expected Return"
                            required
                        />
                    </div>

                    <div className='input-group'>
                        <label className="input-label">Start Date</label>
                        <input
                            type="date"
                            className="date-input"
                            name="startDate"
                            value={formData.startDate}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className='input-group'>
                        <label className="input-label">Expected End Date</label>
                        <input
                            type="date"
                            className="date-input"
                            name="endDate"
                            value={formData.endDate}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Description</label>
                        <textarea
                            className="text-area"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Enter Investment Description"
                        />
                    </div>

                    <button 
                      type="submit" 
                      className='form-button'
                      disabled={updating}
                    >
                      {updating ? 'Updating...' : 'Update Investment'}
                    </button>
                </form>
            </div>
        </div>
    </div>
  );
};

export default EditInvestment;